import { IconButton, Spinner, Tabs } from "@design";
import { Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { CenteredScreen } from "@/components/CenteredScreen";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";
import { WithSidePanel } from "@/components/WithSidePanel";

/**
 * The shared Build detail-page scaffold: breadcrumb (Build / type /
 * entity name + actions) over a tabbed body, with the tab in the URL so views
 * are linkable and Back works. Owns the loading/error screens and the delete
 * affordance; each entity supplies its query result, tabs, and tab content.
 *
 * Keeps the scaffold strict on purpose — it renders the header/tabs and the
 * per-entity tab content as children, nothing more — so it can't drift into a
 * config-object monster.
 */
export function EntityDetailPage({
  listPath,
  rootLabel,
  name,
  isPending,
  isError,
  notFoundLabel,
  tabs,
  canDelete,
  onDelete,
  deleteDialog,
  extraActions,
  aside,
  maxWidth = "max-w-5xl",
}: Readonly<{
  /** Path back to the entity list (e.g. "/build/agents"). */
  listPath: string;
  /** The breadcrumb root label (the entity type, e.g. "Agents"). */
  rootLabel: string;
  /** The entity's display name (breadcrumb leaf + tabs aria-label). */
  name: string;
  isPending: boolean;
  isError: boolean;
  notFoundLabel: string;
  /** The tabs, in order; the first is the default (no path segment). Labels come
      from the entity's own namespace; `content` is the tab's element (null until loaded). */
  tabs: ReadonlyArray<{ value: string; label: string; content: ReactNode }>;
  canDelete?: boolean;
  onDelete?: () => void;
  /** The delete-confirm dialog element (from useDeleteEntity). */
  deleteDialog?: ReactNode;
  /** Extra BreadcrumbBar actions rendered before the delete button (e.g. "Open"). */
  extraActions?: ReactNode;
  /** A side panel beside the page (see WithSidePanel). */
  aside?: ReactNode;
  maxWidth?: string;
}>) {
  const { t } = useTranslation("build");
  const navigate = useNavigate();
  const areas = useAreaCrumbs();
  const { uuid = "", tab } = useParams();
  const values = tabs.map((tb) => tb.value);
  const activeTab = tab && values.includes(tab) ? tab : values[0];

  if (isPending) {
    return (
      <CenteredScreen>
        <Spinner size="lg" />
      </CenteredScreen>
    );
  }
  if (isError) {
    return (
      <CenteredScreen>
        <Text tone="error" role="alert">
          {notFoundLabel}
        </Text>
      </CenteredScreen>
    );
  }

  return (
    <WithSidePanel panel={aside}>
      <PageBreadcrumbs
        trail={[areas.build, { label: rootLabel, to: listPath }]}
        current={name}
        actions={
          extraActions || canDelete ? (
            <>
              {extraActions}
              {canDelete && onDelete && (
                <IconButton
                  icon={<Trash2 />}
                  aria-label={t("menu.delete")}
                  variant="ghost"
                  size="md"
                  onClick={onDelete}
                />
              )}
            </>
          ) : undefined
        }
      />
      <div className={`mx-auto flex w-full ${maxWidth} flex-col gap-6 px-4 py-6 md:px-8`}>
        <Tabs
          aria-label={name}
          tabs={tabs.map((tb) => ({ value: tb.value, label: tb.label }))}
          value={activeTab}
          onChange={(next) => navigate(next === values[0] ? `${listPath}/${uuid}` : `${listPath}/${uuid}/${next}`)}
        />
        {tabs.find((tb) => tb.value === activeTab)?.content}
      </div>
      {deleteDialog}
    </WithSidePanel>
  );
}
